import { CATEGORY_BY_ID } from '../categories';
import type { Registration } from '../registry';
import { ADC1_PIN_OPTIONS, OUTPUT_PIN_OPTIONS } from '../pins';

const c = CATEGORY_BY_ID.analog.colour;

export const analogRegistration: Registration = {
  definitions: [
    {
      type: 'esp32_analog_read',
      message0: 'analog read pin %1',
      args0: [{ type: 'field_dropdown', name: 'PIN', options: ADC1_PIN_OPTIONS }],
      output: 'Number',
      colour: c,
      tooltip: '12-bit ADC reading (0-4095) from an ADC1 pin. ADC2 pins are unusable while Wi-Fi is on and are therefore not offered.',
    },
    {
      type: 'esp32_analog_write',
      message0: 'analog write pin %1 duty %2',
      args0: [
        { type: 'field_dropdown', name: 'PIN', options: OUTPUT_PIN_OPTIONS },
        { type: 'input_value', name: 'DUTY', check: 'Number' },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'PWM output via LEDC. If the pin was not configured with a PWM setup block, it is attached automatically at 5 kHz / 12-bit.',
    },
    {
      type: 'esp32_pwm_setup',
      message0: 'PWM setup pin %1 freq %2 Hz res %3',
      args0: [
        { type: 'field_dropdown', name: 'PIN', options: OUTPUT_PIN_OPTIONS },
        { type: 'field_number', name: 'FREQ', value: 5000, min: 1, max: 20000, precision: 1 },
        {
          type: 'field_dropdown',
          name: 'RES',
          options: [
            ['8-bit (0-255)', '8'],
            ['10-bit (0-1023)', '10'],
            ['12-bit (0-4095)', '12'],
            ['16-bit (0-65535)', '16'],
          ],
        },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Attach a pin to an LEDC channel with a frequency and resolution (Arduino-ESP32 core 3.x API).',
    },
    {
      type: 'esp32_pwm_write',
      message0: 'PWM write pin %1 duty %2',
      args0: [
        { type: 'field_dropdown', name: 'PIN', options: OUTPUT_PIN_OPTIONS },
        { type: 'input_value', name: 'DUTY', check: 'Number' },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Set the LEDC duty cycle for a pin.',
    },
    {
      type: 'esp32_pwm_release',
      message0: 'PWM release pin %1',
      args0: [{ type: 'field_dropdown', name: 'PIN', options: OUTPUT_PIN_OPTIONS }],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Detach the LEDC channel from a pin.',
    },
  ],
  metas: [
    {
      type: 'esp32_analog_read',
      category: 'analog',
      label: 'Analog read (ADC)',
      description: 'Value: 12-bit ADC reading (0-4095) from an ADC1 pin (32, 33, 34, 35, 36, 39).',
      fields: { PIN: { kind: 'dropdown', options: ADC1_PIN_OPTIONS.map((o) => o[1]) } },
    },
    {
      type: 'esp32_analog_write',
      category: 'analog',
      label: 'Analog write (PWM)',
      description: 'Write a PWM duty (0-4095) to a pin; auto-attaches LEDC at 5 kHz / 12-bit if not configured.',
      fields: { PIN: { kind: 'dropdown', options: OUTPUT_PIN_OPTIONS.map((o) => o[1]) }, DUTY: { kind: 'value', types: 'Number' } },
    },
    {
      type: 'esp32_pwm_setup',
      category: 'analog',
      label: 'PWM setup',
      description: 'Attach a pin to LEDC with a frequency (Hz) and resolution in bits (8/10/12/16).',
      fields: { PIN: { kind: 'dropdown', options: OUTPUT_PIN_OPTIONS.map((o) => o[1]) }, FREQ: { kind: 'number' }, RES: { kind: 'dropdown', options: ['8', '10', '12', '16'] } },
    },
    {
      type: 'esp32_pwm_write',
      category: 'analog',
      label: 'PWM write',
      description: 'Set the LEDC duty cycle for a pin (range depends on configured resolution).',
      fields: { PIN: { kind: 'dropdown', options: OUTPUT_PIN_OPTIONS.map((o) => o[1]) }, DUTY: { kind: 'value', types: 'Number' } },
    },
    {
      type: 'esp32_pwm_release',
      category: 'analog',
      label: 'PWM release',
      description: 'Detach the LEDC channel from a pin.',
      fields: { PIN: { kind: 'dropdown', options: OUTPUT_PIN_OPTIONS.map((o) => o[1]) } },
    },
  ],
};
